import React from 'react';
import { motion, useReducedMotion } from 'framer-motion';
import { CheckIcon, ShieldAlertIcon, StarIcon } from 'lucide-react';
import { viewportOnce } from '../../utils/motion';

/**
 * Miniature, non-interactive scenes that animate once in view.
 * Each one shows the actual artefact of its step rather than a decorative shape.
 */

const ease = [0.23, 1, 0.32, 1] as const;
function useEnter() {
  const reduce = useReducedMotion();
  return {
    reduce: !!reduce,
    container: {
      initial: 'hidden' as const,
      whileInView: 'show' as const,
      viewport: viewportOnce,
      variants: {
        hidden: {},
        show: {
          transition: {
            staggerChildren: reduce ? 0 : 0.07,
            delayChildren: 0.05
          }
        }
      }
    },
    item: reduce ? {
      hidden: {
        opacity: 0
      },
      show: {
        opacity: 1,
        transition: {
          duration: 0.15
        }
      }
    } : {
      hidden: {
        opacity: 0,
        y: 10
      },
      show: {
        opacity: 1,
        y: 0,
        transition: {
          type: 'spring' as const,
          stiffness: 320,
          damping: 26
        }
      }
    }
  };
}
function Frame({
  children,
  label
}: {
  children: React.ReactNode;
  label: string;
}) {
  return <figure className="overflow-hidden rounded-2xl border border-hairline bg-surface shadow-lift" data-id="element-646" data-mp="39zlublspqskp">
      <div className="flex items-center gap-1.5 border-b border-hairline bg-oat px-4 py-2.5" data-id="element-647" data-mp="3onyn69wi7vaz">
        <span className="h-2 w-2 rounded-full bg-hairline" aria-hidden="true" data-id="element-648" data-mp="3jxrw83eirypk" />
        <span className="h-2 w-2 rounded-full bg-hairline" aria-hidden="true" data-id="element-649" data-mp="3jvox8tve2v8p" />
        <span className="ml-2 text-[11px] font-semibold uppercase tracking-[0.06em] text-ink-3" data-id="element-650" data-mp="1hpj49maazsl">{label}</span>
      </div>
      <div className="p-4 sm:p-5" data-id="element-651" data-mp="3tpnzrtzdkh16">{children}</div>
      <figcaption className="sr-only" data-id="element-652" data-mp="bgdmc29m1ac8">{label}</figcaption>
    </figure>;
}
function Row({
  label,
  value,
  strong
}: {
  label: string;
  value: string;
  strong?: boolean;
}) {
  return <div className="flex items-baseline justify-between gap-4 py-1.5" data-id="element-653" data-mp="2d89xharv8i6h">
      <span className={strong ? 'text-sm font-medium text-ink' : 'text-[13px] text-ink-2'} data-id="element-654" data-mp="289yot3db4dpu">{label}</span>
      <span className={`tnum ${strong ? 'font-display text-base font-semibold text-ink' : 'text-[13px] text-ink'}`} data-id="element-655" data-mp="c73mza7by2kz">{value}</span>
    </div>;
}

/** Step 1 — the request, with the dietary record attaching itself. */
export function RequestScene() {
  const {
    container,
    item,
    reduce
  } = useEnter();
  return <Frame label="Request a quote" data-id="element-656" data-mp="q74peu38ti2k">
      <motion.div {...container} className="space-y-3" data-id="element-657" data-mp="q86cxy1g3x16">
        <motion.div variants={item} className="grid grid-cols-2 gap-3" data-id="element-658" data-mp="v67v8gr1rsbl">
          {[['Occasion', 'Birthday'], ['Guests', '6'], ['Date', 'Sat 19 Sep'], ['Service', 'At my place']].map(([label, value]) => <div key={label} className="rounded-lg border border-hairline px-3 py-2" data-id="element-659" data-mp="14xvfnxp6s93k">
              <span className="block text-[11px] uppercase tracking-[0.05em] text-ink-3" data-id="element-660" data-mp="18wbecyumr5wt">{label}</span>
              <span className="mt-0.5 block text-sm font-medium text-ink" data-id="element-661" data-mp="1du32riw2g9w9">{value}</span>
            </div>)}
        </motion.div>

        <motion.div variants={item} className="rounded-lg border border-hairline p-3" data-id="element-662" data-mp="1szqp87zyck55">
          <span className="block text-[11px] uppercase tracking-[0.05em] text-ink-3" data-id="element-663" data-mp="1o0e2dn92zd2k">Menu</span>
          <span className="mt-0.5 block text-sm font-medium text-ink" data-id="element-664" data-mp="1j11dyy2k9xxv">Baan Ploy tasting</span>
          <span className="tnum mt-0.5 block text-[13px] text-ink-2" data-id="element-665" data-mp="1ezfxl2qtj1ix">฿2,400 / guest · 6 courses</span>
        </motion.div>

        <motion.div initial={reduce ? {
        opacity: 0
      } : {
        opacity: 0,
        x: 28,
        rotate: -3
      }} whileInView={{
        opacity: 1,
        x: 0,
        rotate: 0
      }} viewport={viewportOnce} transition={reduce ? {
        duration: 0.18
      } : {
        type: 'spring',
        stiffness: 300,
        damping: 22,
        delay: 0.4
      }} className="flex items-start gap-2 rounded-lg border border-danger/40 bg-danger-soft px-3 py-2.5" data-id="element-666" data-mp="2bq8d9z9uark6">
          <ShieldAlertIcon className="mt-0.5 h-4 w-4 shrink-0 text-danger" aria-hidden="true" data-id="element-667" data-mp="2uh865bkggliq" />
          <span className="text-[12px] leading-snug text-danger" data-id="element-668" data-mp="2pof2kr0q3j88">
            <span className="font-semibold" data-id="element-669" data-mp="2a7q8grd0zuxf">Attached automatically · </span>
            shellfish (anaphylaxis), peanuts (strict), dairy
          </span>
        </motion.div>
      </motion.div>
    </Frame>;
}

/** Step 2 — the itemised quote arriving line by line. */
export function QuoteScene() {
  const {
    container,
    item,
    reduce
  } = useEnter();
  const lines = [['Baan Ploy tasting · 6 × 2,400', '฿14,400'], ['Extra service hour', '฿1,200'], ['Wine pairing · 6 × 950', '฿5,700'], ['Travel · 3 km beyond area', '฿75'], ['Service fee & VAT', '฿3,770']];
  return <Frame label="Custom quote · v2" data-id="element-670" data-mp="2k5mitgy52luh">
      <motion.div {...container} data-id="element-671" data-mp="2k4la3j4libfx">
        <motion.div variants={item} className="flex items-baseline justify-between gap-3 pb-2" data-id="element-672" data-mp="2k3jwp803xcv1">
          <span className="text-sm font-medium text-ink" data-id="element-673" data-mp="2o553cbm1suws">6 guests · Sat 19 Sep, 19:00</span>
          <span className="rounded-full bg-accent-soft px-2 py-0.5 text-[11px] font-semibold text-on-accent" data-id="element-674" data-mp="3bvj02ffkvhxp">Expires in 41 hrs</span>
        </motion.div>
        <div className="divide-y divide-hairline border-y border-hairline" data-id="element-675" data-mp="1581w7hilebgt">
          {lines.map(([label, value]) => <motion.div key={label} variants={item} data-id="element-676" data-mp="108p70q490sb2">
              <Row label={label} value={value} data-id="element-677" data-mp="1eyd75o0p1kx7" />
            </motion.div>)}
        </div>
        <motion.div variants={item} className="pt-2" data-id="element-678" data-mp="19za1q2gsxcop">
          <Row label="Total" value="฿25,145" strong data-id="element-679" data-mp="19zs8ht13b9av" />
        </motion.div>
        <motion.div variants={item} className="mt-2 grid grid-cols-2 gap-3 rounded-lg bg-oat p-3" data-id="element-680" data-mp="m8wage61jhv6">
          <div data-id="element-681" data-mp="hbeavedwyezu">
            <span className="block text-[12px] text-ink-2" data-id="element-682" data-mp="hbwc6pffnjem">Deposit now</span>
            <span className="tnum block text-sm font-semibold text-ink" data-id="element-683" data-mp="w85anori1spz">฿7,543</span>
          </div>
          <div data-id="element-684" data-mp="r8iw9bzxq6fl">
            <span className="block text-[12px] text-ink-2" data-id="element-685" data-mp="r7zfhym0to35">After service</span>
            <span className="tnum block text-sm font-semibold text-ink" data-id="element-686" data-mp="27m948tu6deui">฿17,602</span>
          </div>
        </motion.div>
        <motion.div initial={reduce ? {
        opacity: 0
      } : {
        opacity: 0,
        scale: 0.96
      }} whileInView={{
        opacity: 1,
        scale: 1
      }} viewport={viewportOnce} transition={reduce ? {
        duration: 0.18
      } : {
        type: 'spring',
        stiffness: 380,
        damping: 22,
        delay: 0.55
      }} className="mt-3 flex items-center gap-2 rounded-lg bg-primary-soft px-3 py-2 text-[12px] font-medium text-primary" data-id="element-687" data-mp="22n5yf76df4lh">
          <CheckIcon className="h-4 w-4" aria-hidden="true" data-id="element-688" data-mp="2ombwpsqvsp9j" />
          Chef acknowledged the dietary record before sending
        </motion.div>
      </motion.div>
    </Frame>;
}

/** Step 3 — deposit taken, date held, timeline moving. */
export function ConfirmScene() {
  const {
    container,
    item,
    reduce
  } = useEnter();
  const steps = ['Requested', 'Quoted', 'Confirmed', 'In progress', 'Completed'];
  const done = 3;
  return <Frame label="Booking PA-1042" data-id="element-689" data-mp="18ael8xdjikow">
      <motion.div {...container} className="space-y-4" data-id="element-690" data-mp="189ddb1rtmebe">
        <motion.div variants={item} className="flex items-baseline justify-between gap-3" data-id="element-691" data-mp="1d8zoz6s03mi3">
          <span className="font-display text-lg font-semibold text-ink" data-id="element-692" data-mp="3qjix3zcom6mr">Date held</span>
          <span className="tnum text-[13px] text-ink-2" data-id="element-693" data-mp="3qh6j1xa1ogez">Deposit ฿7,543 authorised</span>
        </motion.div>

        <div className="relative" data-id="element-694" data-mp="3hiuip3jdm49c">
          <span className="absolute left-[7px] top-2 h-[calc(100%-1rem)] w-px bg-hairline" aria-hidden="true" data-id="element-695" data-mp="3hhrvp6p6clyp" />
          <motion.span aria-hidden="true" initial={reduce ? {
          opacity: 0
        } : {
          scaleY: 0
        }} whileInView={reduce ? {
          opacity: 1
        } : {
          scaleY: 1
        }} viewport={viewportOnce} transition={reduce ? {
          duration: 0.2
        } : {
          duration: 0.6,
          ease,
          delay: 0.2
        }} style={{
          transformOrigin: 'top'
        }} className="absolute left-[7px] top-2 h-[calc((100%-1rem)*0.5)] w-px bg-primary" data-id="element-696" data-mp="3ciyh1ay5iowy" />
          <ol className="space-y-2.5" data-id="element-697" data-mp="2otvzs47fiy2z">
            {steps.map((label, i) => <motion.li key={label} variants={item} className="flex items-center gap-3" data-id="element-698" data-mp="2ow024hpojp15" data-mpi={i}>
                <span className={`relative z-10 flex h-[15px] w-[15px] items-center justify-center rounded-full border-2 ${i < done ? 'border-primary bg-primary' : 'border-hairline bg-surface'}`} aria-hidden="true" data-id="element-699" data-mp="2jx5jbfrpq0h1">
                  {i < done && <CheckIcon className="h-2.5 w-2.5 text-on-primary" data-id="element-700" data-mp="7kiyxl7vq47n" />}
                </span>
                <span className={`text-[13px] ${i < done ? 'font-medium text-ink' : 'text-ink-3'}`} data-id="element-701" data-mp="3p1ajgicnfn7a">{label}</span>
              </motion.li>)}
          </ol>
        </div>

        <motion.p variants={item} className="rounded-lg bg-oat p-3 text-[12px] leading-snug text-ink-2" data-id="element-702" data-mp="3tttzrn2q0wfs">
          Groceries on this menu are billed at cost — receipts appear here the same night, and the balance is charged after
          service.
        </motion.p>
      </motion.div>
    </Frame>;
}

/** Step 4 — four subscores, filling. */
export function ReviewScene() {
  const {
    container,
    item,
    reduce
  } = useEnter();
  const scores: [string, number][] = [['Food', 5], ['Punctuality', 4.8], ['Cleanliness', 5], ['Communication', 4.9]];
  return <Frame label="Leave a review" data-id="element-703" data-mp="2nfp8zdvyw8cu">
      <motion.div {...container} className="space-y-3" data-id="element-704" data-mp="31dvxi3f10vzu">
        <motion.div variants={item} className="flex items-center gap-1" data-id="element-705" data-mp="31fgrnrxficx6">
          {[0, 1, 2, 3, 4].map(i => <motion.span key={i} initial={reduce ? {
          opacity: 0
        } : {
          opacity: 0,
          scale: 0.7
        }} whileInView={{
          opacity: 1,
          scale: 1
        }} viewport={viewportOnce} transition={reduce ? {
          duration: 0.15
        } : {
          type: 'spring',
          stiffness: 420,
          damping: 18,
          delay: 0.1 + i * 0.06
        }} data-id="element-706" data-mp="2wgvpvo2j3l6m">
              <StarIcon className="h-6 w-6 fill-accent text-accent" aria-hidden="true" data-id="element-707" data-mp="3f50qwnrsws1q" />
            </motion.span>)}
        </motion.div>

        <dl className="space-y-2.5" data-id="element-708" data-mp="3u0gov3vi583e">
          {scores.map(([label, value]) => <motion.div key={label} variants={item} data-id="element-709" data-mp="3p5b3q79xbxgd">
              <div className="flex items-baseline justify-between gap-3" data-id="element-710" data-mp="2dinu3s31np7e">
                <dt className="text-[13px] text-ink-2" data-id="element-711" data-mp="28re42qdl2juz">{label}</dt>
                <dd className="tnum text-[13px] font-semibold text-ink" data-id="element-712" data-mp="28q30oro1ve4e">{value.toFixed(1)}</dd>
              </div>
              <div className="mt-1 h-1.5 overflow-hidden rounded-full bg-oat" data-id="element-713" data-mp="23q8civvnt2n1">
                <motion.div initial={reduce ? {
              opacity: 0
            } : {
              width: 0
            }} whileInView={reduce ? {
              opacity: 1,
              width: `${value / 5 * 100}%`
            } : {
              width: `${value / 5 * 100}%`
            }} viewport={viewportOnce} transition={reduce ? {
              duration: 0.2
            } : {
              duration: 0.55,
              ease,
              delay: 0.25
            }} className="h-full rounded-full bg-primary" data-id="element-714" data-mp="1zsalrom8q8hj" />
              </div>
            </motion.div>)}
        </dl>

        <motion.p variants={item} className="text-[12px] leading-snug text-ink-3" data-id="element-715" data-mp="1c2gavclhetzx">
          Scored separately, by guests who actually completed a booking — which is why “4.8” tells you something here.
        </motion.p>
      </motion.div>
    </Frame>;
}

/** Chef step 1 — the menu and its price, as published. */
export function MenuPriceScene() {
  const {
    container,
    item
  } = useEnter();
  return <Frame label="Menu editor" data-id="element-716" data-mp="3rnzaaajfm0wp">
      <motion.div {...container} className="space-y-3" data-id="element-717" data-mp="b61w6kgxr67">
        <motion.div variants={item} data-id="element-718" data-mp="cpglsqh7u5v">
          <span className="text-[11px] uppercase tracking-[0.05em] text-ink-3" data-id="element-719" data-mp="5araz9tcrfwn">Menu name</span>
          <p className="font-display text-lg font-semibold text-ink" data-id="element-720" data-mp="a9lvcg6yxciv">Isan feast over charcoal</p>
        </motion.div>
        <motion.div variants={item} className="grid grid-cols-3 gap-3" data-id="element-721" data-mp="f85i2itdysca">
          {[['Per guest', '฿1,250'], ['Min guests', '2'], ['Hours', '3']].map(([label, value]) => <div key={label} className="rounded-lg border border-hairline px-3 py-2" data-id="element-722" data-mp="2h1d56ef8qe8q">
              <span className="block text-[11px] uppercase tracking-[0.05em] text-ink-3" data-id="element-723" data-mp="2l0vh4kp33ezf">{label}</span>
              <span className="tnum mt-0.5 block text-sm font-semibold text-ink" data-id="element-724" data-mp="3odlz1z5ig136">{value}</span>
            </div>)}
        </motion.div>
        <motion.div variants={item} className="rounded-lg bg-oat p-3" data-id="element-725" data-mp="3jfkd96gp9no0">
          <span className="block text-[11px] uppercase tracking-[0.05em] text-ink-3" data-id="element-726" data-mp="19eh9dtev97o">Groceries</span>
          <span className="mt-0.5 block text-sm font-medium text-ink" data-id="element-727" data-mp="3ta2n6tcpmfg9">Included in the price</span>
        </motion.div>
        <motion.div variants={item} className="flex items-baseline justify-between rounded-lg border border-primary/40 bg-primary-soft px-3 py-2.5" data-id="element-728" data-mp="b3y1ypqveqr9">
          <span className="text-[13px] font-medium text-primary" data-id="element-729" data-mp="kw3r7rpqmu2r">Guests will see</span>
          <span className="tnum font-display text-base font-semibold text-primary" data-id="element-730" data-mp="fw7n7sh1j2n7">from ฿1,250 / guest</span>
        </motion.div>
      </motion.div>
    </Frame>;
}

/** Chef step 2 — service areas as rings on a stripped-back map. */
export function AreaScene() {
  const reduce = useReducedMotion();
  const rings = [{
    cx: 92,
    cy: 96,
    r: 46,
    label: 'Ari',
    dashed: false
  }, {
    cx: 176,
    cy: 130,
    r: 34,
    label: 'Sukhumvit',
    dashed: false
  }, {
    cx: 128,
    cy: 176,
    r: 26,
    label: 'Silom',
    dashed: true
  }];
  return <Frame label="Service areas" data-id="element-731" data-mp="zhhovza3u9ko">
      <svg viewBox="0 0 260 220" className="h-auto w-full" role="img" aria-label="Three service-area rings over a simplified map" data-id="element-732" data-mp="zklw75iv9lfk">
        <path d="M18 -10 C48 50, 40 96, 74 132 C104 164, 68 194, 84 230" fill="none" stroke="rgb(var(--c-primary) / 0.18)" strokeWidth="14" strokeLinecap="round" data-id="element-733" data-mp="pszp9ldka8im" />
        <path d="M60 40 L176 92 L232 168" fill="none" stroke="rgb(var(--c-hairline))" strokeWidth="2" strokeDasharray="6 6" data-id="element-734" data-mp="fvl9zp3djprw" />
        {rings.map((ring, i) => <g key={ring.label} data-id="element-735" data-mp="6tviykdemsun" data-mpi={i}>
            <motion.circle cx={ring.cx} cy={ring.cy} r={ring.r} initial={reduce ? {
          opacity: 0
        } : {
          scale: 0.4,
          opacity: 0
        }} whileInView={{
          scale: 1,
          opacity: 1
        }} viewport={viewportOnce} transition={reduce ? {
          duration: 0.2
        } : {
          type: 'spring',
          stiffness: 260,
          damping: 20,
          delay: 0.1 + i * 0.12
        }} style={{
          transformOrigin: `${ring.cx}px ${ring.cy}px`
        }} fill={ring.dashed ? 'rgb(var(--c-action) / 0.1)' : 'rgb(var(--c-primary) / 0.12)'} stroke={ring.dashed ? 'rgb(var(--c-action))' : 'rgb(var(--c-primary))'} strokeWidth="1.6" strokeDasharray={ring.dashed ? '5 5' : undefined} data-id="element-736" data-mp="6tlskvog6jof" />
            <circle cx={ring.cx} cy={ring.cy} r="3.5" fill="rgb(var(--c-primary))" data-id="element-737" data-mp="378zieyiprsk1" />
            <text x={ring.cx} y={ring.cy + ring.r + 14} textAnchor="middle" fontSize="11" fontWeight="600" fill="rgb(var(--c-ink-2))" data-id="element-738" data-mp="37b28fijv9lor">
              {ring.label}
            </text>
          </g>)}
      </svg>
      <p className="mt-1 text-[12px] text-ink-3" data-id="element-739" data-mp="11g14iz7c1qj2">
        Solid rings include travel. Dashed rings add ฿20/km to the quote automatically.
      </p>
    </Frame>;
}

/** Chef step 3 — the quote builder totalling up. */
export function BuilderScene() {
  const {
    container,
    item
  } = useEnter();
  return <Frame label="Quote builder" data-id="element-740" data-mp="2t3penbrskwnc">
      <motion.div {...container} className="space-y-2.5" data-id="element-741" data-mp="2t4iedssc2lvz">
        {[['Menu', 'Isan feast · 8 guests', true], ['Extra hour', '+ ฿900', false], ['Charcoal setup', '+ ฿1,200', false], ['Server for the night', '+ ฿3,500', false]].map(([label, value, strong]) => <motion.div key={label as string} variants={item} className={`flex items-baseline justify-between gap-3 rounded-lg px-3 py-2 ${strong ? 'bg-oat' : 'border border-hairline'}`} data-id="element-742" data-mp="25eeloggoo6jr">
            <span className="text-[13px] text-ink-2" data-id="element-743" data-mp="3kv50v4pfshd1">{label as string}</span>
            <span className="tnum text-sm font-medium text-ink" data-id="element-744" data-mp="3ptq0p30s0z0y">{value as string}</span>
          </motion.div>)}
        <motion.div variants={item} className="flex items-start gap-2 rounded-lg border border-accent/50 bg-accent-soft px-3 py-2.5" data-id="element-745" data-mp="1pqxhcn29bgfl">
          <span className="text-[12px] leading-snug text-on-accent" data-id="element-746" data-mp="1lpby8jz4q5wy">
            <span className="font-semibold" data-id="element-747" data-mp="1zpwi7jgs67s1">Kitchen check · </span>
            this menu needs a charcoal grill and a granite mortar. Their kitchen lists neither — bring your own.
          </span>
        </motion.div>
        <motion.div variants={item} data-id="element-748" data-mp="1720kqcje0bu7">
          <Row label="Send for" value="฿13,000" strong data-id="element-749" data-mp="16ywdt7ejf1zu" />
        </motion.div>
      </motion.div>
    </Frame>;
}

/** Chef step 4 — payouts on a schedule. */
export function PayoutScene() {
  const {
    container,
    item,
    reduce
  } = useEnter();
  const bars = [42, 58, 66, 61, 82, 100];
  const months = ['Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug'];
  return <Frame label="Earnings" data-id="element-750" data-mp="o9fyhdy714zd">
      <motion.div {...container} className="space-y-4" data-id="element-751" data-mp="o8n0p2h7pptd">
        <motion.div variants={item} data-id="element-752" data-mp="jc6bs626v7f8">
          <span className="text-[11px] uppercase tracking-[0.05em] text-ink-3" data-id="element-753" data-mp="jb4nv102qqg3">Take-home, August</span>
          <p className="tnum font-display text-2xl font-semibold text-primary" data-id="element-754" data-mp="2uxf9w4nakc3h">฿248,000</p>
        </motion.div>
        <div className="flex h-28 items-end gap-2" aria-hidden="true" data-id="element-755" data-mp="2zrjh8p0gym53">
          {bars.map((h, i) => <div key={months[i]} className="flex flex-1 flex-col items-center gap-1.5" data-id="element-756" data-mp="2lw8n5du1u51s" data-mpi={i}>
              <motion.div initial={reduce ? {
            opacity: 0
          } : {
            height: 0
          }} whileInView={reduce ? {
            opacity: 1,
            height: `${h}%`
          } : {
            height: `${h}%`
          }} viewport={viewportOnce} transition={reduce ? {
            duration: 0.2
          } : {
            duration: 0.5,
            ease,
            delay: 0.08 * i
          }} className={`w-full rounded-t ${i === bars.length - 1 ? 'bg-primary' : 'bg-primary/30'}`} data-id="element-757" data-mp="2pxkhtxrs5ggu" />
              <span className="text-[10px] text-ink-3" data-id="element-758" data-mp="1t8uwtykuw7qn">{months[i]}</span>
            </div>)}
        </div>
        <motion.div variants={item} className="divide-y divide-hairline border-t border-hairline" data-id="element-759" data-mp="1y9imogxcmija">
          <Row label="16–31 Aug · paid" value="฿118,600" data-id="element-760" data-mp="2ozlwm9fzefkj" />
          <Row label="1–15 Sep · scheduled" value="฿41,200" data-id="element-761" data-mp="2kwz3843nwml8" />
        </motion.div>
      </motion.div>
    </Frame>;
}
export const guestScenes = [RequestScene, QuoteScene, ConfirmScene, ReviewScene];
export const chefScenes = [MenuPriceScene, AreaScene, BuilderScene, PayoutScene];